import { useState, useCallback } from 'react';
import { debounce } from 'lodash';
import { Check, Plus } from 'lucide-react';

import { useSelector } from 'react-redux';
import { selectUserToken } from '../../../../../redux/features/user/userSelectors';
import { useGetSearchInputRealtiesQuery } from '../../../../../redux/services/search/searchApi';
import { useEmailContext } from '../../../../../contexts/EmailContext';
import { useRequestLikes } from '../../../../../hooks/useRequestLikes';

import { SearchItem } from '../../../search/realties/SearchItem';
import { Loader } from '../../../../ui/Loader';

export const RealtyPickerInput = () => {
  const { selectedRealties, addRealty, removeRealty, requestId } = useEmailContext();
  const { likedRealties } = useRequestLikes(requestId);

  const userToken = useSelector(selectUserToken);
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [isListOpen, setIsListOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const hasLiked = likedRealties.length > 0;

  const likedIds = new Set(likedRealties.map(r => r.id));

  const {
    data: searchResults = {},
    isFetching: isSearchFetching,
  } = useGetSearchInputRealtiesQuery(
    { token: userToken, search_query: searchQuery },
    { skip: !searchQuery || searchQuery.length < 3 }
  );

  // Filter out liked realties from search results when in expanded mode
  const searchRealties = (searchResults?.realties ?? []).filter(
    r => !likedIds.has(r.id)
  );

  const handleDebounceSearch = useCallback(
    debounce(value => {
      setSearchQuery(value);
      setIsListOpen(value.length >= 3);
    }, 800),
    []
  );

  const handleInputChange = e => {
    const value = e.target.value;
    setInputValue(value);
    handleDebounceSearch(value);
  };

  const handleToggleSearch = () => {
    setIsSearchExpanded(v => !v);
    setInputValue('');
    setSearchQuery('');
    setIsListOpen(false);
  };

  const isSelected = id => selectedRealties.some(r => r.id === id);

  const handleLikedClick = realty => {
    if (isSelected(realty.id)) {
      removeRealty(realty.id);
    } else {
      addRealty(realty);
    }
  };

  const handleSearchItemClick = realty => {
    addRealty(realty);
    setIsListOpen(false);
    setInputValue('');
    setSearchQuery('');
  };

  return (
    <div className="flex flex-col gap-3 w-full">

      {/* ── Liked realties list ── */}
      {hasLiked && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-blackColor">
              Liked properties for this request
            </span>
            <button
              type="button"
              onClick={handleToggleSearch}
              title="Search and add more properties"
              className={`flex items-center justify-center w-5 h-5 rounded-full border transition-colors ${
                isSearchExpanded
                  ? 'bg-purpleColor border-purpleColor text-white'
                  : 'border-gray-400 text-gray-400 hover:border-purpleColor hover:text-purpleColor'
              }`}
            >
              <Plus size={12} />
            </button>
          </div>

          <div className="flex flex-col border border-gray-200 rounded overflow-hidden w-full sm:w-1/2">
            {likedRealties.map(realty => (
              <div key={realty.id} className="relative flex items-center">
                <div className="flex-1">
                  <SearchItem
                    item={realty}
                    handleItemClick={() => handleLikedClick(realty)}
                  />
                </div>
                {isSelected(realty.id) && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Check size={16} className="text-purpleColor" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Search (always shown when no liked, or when expanded) ── */}
      {(!hasLiked || isSearchExpanded) && (
        <div className="flex flex-col gap-2">
          {!hasLiked && (
            <span className="text-sm font-medium text-blackColor">Add property</span>
          )}

          <input
            type="text"
            placeholder="Search properties..."
            value={inputValue}
            onChange={handleInputChange}
            className="w-full sm:w-1/2 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-purpleColor"
          />

          {isSearchFetching && (
            <div className="flex items-center justify-center h-10 w-full sm:w-1/2">
              <Loader />
            </div>
          )}

          {!isSearchFetching && isListOpen && searchRealties.length > 0 && (
            <div className="flex flex-col border border-gray-200 rounded overflow-hidden w-full sm:w-1/2">
              {searchRealties.map(realty => (
                <div key={realty.id} className="relative flex items-center">
                  <div className="flex-1">
                    <SearchItem
                      item={realty}
                      handleItemClick={() => handleSearchItemClick(realty)}
                    />
                  </div>
                  {isSelected(realty.id) && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                      <Check size={16} className="text-purpleColor" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {!isSearchFetching && isListOpen && searchRealties.length === 0 && (
            <span className="text-xs text-gray-500">Nothing found</span>
          )}

          {!isSearchFetching && inputValue.length > 0 && inputValue.length < 3 && (
            <span className="text-xs text-gray-400">Type at least 3 characters to search</span>
          )}
        </div>
      )}

    </div>
  );
};
